import * as SentryVue from '@sentry/vue';
import {logUuid, serializeLogContext, type LiteralString, type LogContext} from './logContext';

type ReportLevel = 'info' | 'warning' | 'error';

type ReportErrorOptions = {
    transactionName?: string | null;
};

export function reportError<const Context extends Record<string, unknown>>(
    error: unknown,
    context?: Context & LogContext<Context>,
    options: ReportErrorOptions = {}
): string {
    logWithoutSentry(error);

    return SentryVue.withScope(scope => {
        if (options.transactionName) {
            scope.setTransactionName(options.transactionName);
        }

        return SentryVue.captureException(error, {
            contexts: context ? {details: serializeLogContext(context)} : undefined,
        });
    });
}

export function reportMessage<
    const Message extends string,
    const Context extends Record<string, unknown>,
>(
    message: Message & LiteralString<Message>,
    level: ReportLevel,
    context?: Context & LogContext<Context>
): string {
    logWithoutSentry(message);

    return SentryVue.captureMessage(message, {
        level,
        contexts: context ? {details: serializeLogContext(context)} : undefined,
    });
}

export function addReportBreadcrumb<
    const Message extends string,
    const Context extends Record<string, unknown>,
>(message: Message & LiteralString<Message>, context?: Context & LogContext<Context>): void {
    SentryVue.addBreadcrumb({
        message,
        data: context ? serializeLogContext(context) : undefined,
    });
}

export function setReportingUser(
    user: {userId: string; accountId: string | null | undefined} | null
): void {
    if (!user) {
        SentryVue.setUser(null);
        return;
    }

    SentryVue.setUser({
        id: logUuid(user.userId) ?? undefined,
        account_id: logUuid(user.accountId),
    });
}

// Locally Sentry has no DSN, so the console is the only place a report is visible.
function logWithoutSentry(reported: unknown): void {
    if (SentryVue.getClient()?.getDsn()) {
        return;
    }

    // eslint-disable-next-line no-console
    console.warn(reported);
}
